/* eslint-disable tailwindcss/no-arbitrary-value */
import React, { useState, useEffect, useContext } from 'react';
import { RichText } from '@thd-olt-component-react/rich-text';
import PropTypes from 'prop-types';
import {
  Typography,
  Button,
  DrawerHeader,
  DrawerBody,
  Drawer,
  DrawerFooter,
  Skeleton,
  SkeletonLine,
  SkeletonContent,
} from '@one-thd/sui-atomic-components';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { useContentfulContext } from '../../providers/ContentfulContext';
import { CARD_IMAGE } from '../utils/constants';
import { getDynamicOffer, redirectToAppliances, redirectToSavings } from '../utils/general';
import {
  getPaymentEstimatorDetails,
  getDefaultApplyNowURL,
  SPECIAL_PROMOTION,
  TIERED_PROMOTION,
  APPLIANCE_PROMOTION,
  TIERED_APPLIANCE_PROMOTION,
} from '../utils/plcc-promo-info';
import { suggestedPaymentDetails } from '../utils/terms-and-conditions-utils';

export const TermsAndConditionsDrawerContentful = ({
  closeDrawer,
  open,
  showFinancing,
  applyNowClick,
  isB2B,
  price,
  isAppliance,
  isEcmPromo,
  isNoImpact,
  campaignCode
}) => {
  const [paymentEstimatorValues, setPaymentEstimatorValues] = useState({});
  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';

  const { plccDrawerData, plccData } = useContentfulContext();
  const [applyNowUrl, setApplyNowUrl] = useState();

  useEffect(() => {
    setPaymentEstimatorValues(getPaymentEstimatorDetails(plccData, campaignCode, isMobile, isAppliance, price));

    if (isB2B || !price || !showFinancing) {
      setApplyNowUrl(getDefaultApplyNowURL(plccData, campaignCode, isMobile, isB2B));
    } else {
      setApplyNowUrl(getPaymentEstimatorDetails(plccData, campaignCode, isMobile, isAppliance, price).applyNowUrl);
    }
  }, []);

  const {
    activePromotion,
    financingMonths,
    monthlyPayment,
    activeStartDate,
    activeEndDate
  } = paymentEstimatorValues;

  const dynamicDiscountDetails = () => {
    return (
      <Typography variant="body-lg" align="center">
        <span className="sui-font-bold">${getDynamicOffer(price)} OFF</span>
      </Typography>
    );
  };

  const handleDiscountDetails = () => {
    let discountContent = (
      <RichText content={plccDrawerData?.component?.everydayPromotion?.offer} />
    );
    if (activePromotion === TIERED_PROMOTION) {
      discountContent = <RichText content={plccDrawerData?.component?.tieredPromotion?.offer} />;
    } else if (activePromotion === APPLIANCE_PROMOTION) {
      discountContent = <RichText content={plccDrawerData?.component?.appliancePromotion?.offer} />;
    } else if (activePromotion === TIERED_APPLIANCE_PROMOTION) {
      discountContent = (
        <RichText content={plccDrawerData?.component?.tieredAppliancePromotion?.offer} />
      );
    } else if (activePromotion === SPECIAL_PROMOTION) {
      discountContent = (
        <RichText content={plccDrawerData?.component?.twelveMonthPromotion?.offer} />
      );
    }

    return (
      <Typography variant="body-base">
        <div>
          {plccDrawerData?.component ? (
            <div className="sui-text-left">{discountContent}</div>
          ) : (
            <div className="sui-w-full sui-h-50">
              <Skeleton grow>
                <SkeletonContent initialSize="full" grow disableShrink>
                  <SkeletonLine variant="multi" fullWidth numberOfLines={2} />
                </SkeletonContent>
              </Skeleton>
            </div>
          )}
        </div>
      </Typography>
    );
  };

  const appendDate = (text) => {
    let parsedObject = null;
    try {
      parsedObject = JSON.parse(text);
      parsedObject.content[0].content[1].value += ` Valid: ${activeStartDate} - ${activeEndDate}`;
    } catch (error) {
      return text;
    }
    return JSON.stringify(parsedObject);
  };

  const handleDeferredDisclosure = () => {
    let financingContent = (
      <RichText content={plccDrawerData?.component?.everydayPromotion?.deferredDisclosure} />
    );
    if (activePromotion === TIERED_PROMOTION) {
      financingContent = (
        <RichText
          content={appendDate(plccDrawerData?.component?.tieredPromotion?.deferredDisclosure)}
        />
      );
    } else if (activePromotion === APPLIANCE_PROMOTION) {
      financingContent = (
        <RichText
          content={appendDate(plccDrawerData?.component?.appliancePromotion?.deferredDisclosure)}
        />
      );
    } else if (activePromotion === TIERED_APPLIANCE_PROMOTION) {
      financingContent = (
        <RichText
          content={appendDate(plccDrawerData?.component?.tieredAppliancePromotion?.deferredDisclosure)}
        />
      );
    } else if (activePromotion === SPECIAL_PROMOTION) {
      financingContent = (
        <RichText
          content={appendDate(plccDrawerData?.component?.twelveMonthPromotion?.deferredDisclosure)}
        />
      );
    }
    return (
      <Typography variant="body-base">
        <div>
          {plccDrawerData?.component ? (
            <div className="sui-text-left sui-flex sui-items-center">{financingContent}</div>
          ) : (
            <div className="sui-w-full sui-h-50">
              <Skeleton grow>
                <SkeletonContent initialSize="full" grow disableShrink>
                  <SkeletonLine variant="multi" fullWidth numberOfLines={2} />
                </SkeletonContent>
              </Skeleton>
            </div>
          )}
        </div>
      </Typography>
    );
  };

  const discountTerms = () => {
    return (
      <div>
        {plccDrawerData?.component?.newAccountOfferDisclosure ? (
          <ul className="sui-flex sui-flex-col sui-list-none">
            <li className="sui-flex sui-gap-2px">
              <div className="sui-text-left">
                <RichText content={plccDrawerData?.component?.newAccountOfferDisclosure} />
              </div>
            </li>
          </ul>
        ) : (
          <div className="sui-w-full">
            <Skeleton grow>
              <SkeletonContent initialSize="full" grow disableShrink>
                <SkeletonLine variant="multi" fullWidth numberOfLines={5} />
              </SkeletonContent>
            </Skeleton>
          </div>
        )}
      </div>
    );
  };

  const handleTila = () => {
    let showTila = (
      <div className="sui-border-1 sui-border-solid sui-p-2 sui-border-primary sui-text-xs">
        <div className="[&>p]:sui-text-xs">
          <RichText content={plccDrawerData?.component?.standardTila} />
        </div>
      </div>
    );
    if (showFinancing && price >= 300) {
      showTila = (
        <>
          <div className="sui-border-1 sui-border-solid sui-p-2 sui-border-primary sui-text-xs">
            <div className="[&>p]:sui-text-xs">
              <RichText content={plccDrawerData?.component?.standardTila} />
              <RichText content={plccDrawerData?.component?.paymentEstimatorTila} />
            </div>
          </div>
        </>
      );
    }
    return (
      <Typography variant="body-base">
        <div>
          {plccDrawerData?.component ? (
            <div className="sui-text-left">{showTila}</div>
          ) : (
            <div className="sui-w-full sui-h-50">
              <Skeleton grow>
                <SkeletonContent initialSize="full" grow disableShrink>
                  <SkeletonLine variant="multi" fullWidth numberOfLines={2} />
                </SkeletonContent>
              </Skeleton>
            </div>
          )}
        </div>
      </Typography>
    );
  };

  return (
    <>
      {paymentEstimatorValues ? (
        <Drawer open={open} onClose={closeDrawer} position="right" id="drawer-terms-conditions">
          <DrawerHeader title={plccDrawerData?.component?.title} onClose={closeDrawer} />
          <div className="sui-flex sui-flex-col sui-justify-between sui-h-full">
            <DrawerBody>
              <div className="sui-flex sui-flex-col sui-gap-4">
                <div className="sui-m-auto sui-px-6 sui-max-w-xs">
                  <img
                    src={isB2B ? CARD_IMAGE.PLCR_HOMEDEPOT : CARD_IMAGE.PLCN_HOMEDEPOT}
                    alt="Credit Card Icon"
                    width="185"
                    height="103"
                  />
                </div>
                <div className="sui-flex sui-flex-col sui-gap-3">
                  {price >= 300
                    && showFinancing
                    && suggestedPaymentDetails(monthlyPayment, price, financingMonths)}
                  {price >= 25 && !showFinancing && dynamicDiscountDetails()}
                  {handleDiscountDetails()}
                  {showFinancing && handleDeferredDisclosure()}
                  {handleTila()}
                  {discountTerms()}
                </div>
              </div>
            </DrawerBody>
            <DrawerFooter>
              {isEcmPromo ? (
                <Button
                  variant="primary"
                  data-testid="drawer-apply-now"
                  onClick={isAppliance ? redirectToAppliances : redirectToSavings}
                  fullWidth
                >
                  Shop {isAppliance ? 'Appliance Sales' : 'Specials & Offers'}
                </Button>
              ) : (
                <Button
                  variant="primary"
                  data-testid="drawer-apply-now"
                  href={!isNoImpact && applyNowUrl}
                  onClick={applyNowClick}
                  fullWidth
                >
                  {isNoImpact ? 'Check Now' : 'Apply Now'}
                </Button>
              )}
            </DrawerFooter>
          </div>
        </Drawer>
      ) : null}
    </>
  );
};

TermsAndConditionsDrawerContentful.propTypes = {
  closeDrawer: PropTypes.func.isRequired,
  open: PropTypes.bool,
  showFinancing: PropTypes.bool,
  applyNowClick: PropTypes.func, // Replace with analytics built in?
  isB2B: PropTypes.bool,
  price: PropTypes.number,
  isAppliance: PropTypes.bool,
  isEcmPromo: PropTypes.bool,
  isNoImpact: PropTypes.bool,
  campaignCode: PropTypes.string,
};

TermsAndConditionsDrawerContentful.defaultProps = {
  open: true,
  applyNowClick: () => { },
  isB2B: false,
  showFinancing: false,
  price: null,
  isAppliance: false,
  isEcmPromo: false,
  isNoImpact: false,
  campaignCode: ''
};
